import React from "react";
import {PageRouteProvider} from "@renta-apps/athenaeum-react-common";
import {Button, ButtonType, DropdownWidget, NumberWidget, PageContainer, TextAreaWidget, TitleWidget, WidgetContainer} from "@renta-apps/athenaeum-react-components";
import AuthorizedPage from "../../../models/base/AuthorizedPage";
import Warehouse from "@/models/server/Warehouse";
import WorkOrderType from "@/models/server/WorkOrderType";
import WorkOrderModel from "@/models/server/WorkOrderModel";
import User from "@/models/server/User";
import ConstructionSiteOrWarehouse from "@/models/server/ConstructionSiteOrWarehouse";
import RentaTasksController from "../RentaTasksController";
import Localizer from "../../../localization/Localizer";
import SaveDepotWorkRequest from "@/models/server/requests/SaveDepotWorkRequest";
import SaveDepotWorkResponse from "@/models/server/responses/SaveDepotWorkResponse";
import UserDefaultWorkOrderValues from "@/models/server/UserDefaultWorkOrderValues";
import PageDefinitions from "@/providers/PageDefinitions";
import GetConstructionSitesDataRequest from "@/models/server/requests/GetConstructionSitesDataRequest";

import rentaTaskStyles from "@/pages/RentaTask.module.scss";
import styles from "./DepotWork.module.scss";

interface IDepotWorkProps {
}

interface IDepotWorkState {
    selectedWarehouse: Warehouse | null;
    hours: number;
    comment: string;
    warehouses: Warehouse[];
    isSaving: boolean;
    selectedWorkOrderType: WorkOrderType | null;
    workOrderTypes: WorkOrderType[];
    selectedManager: User | null;
    managers: User[];
    selectedConstructionSite: ConstructionSiteOrWarehouse | null;
    constructionSites: ConstructionSiteOrWarehouse[];
}

export default class DepotWork extends AuthorizedPage<IDepotWorkProps, IDepotWorkState> {

    state: IDepotWorkState = {
        selectedWarehouse: null,
        hours: 0,
        comment: "",
        warehouses: [],
        isSaving: false,
        selectedWorkOrderType: null,
        workOrderTypes: [],
        selectedManager: null,
        managers: [],
        selectedConstructionSite: null,
        constructionSites: []
    };

    public async initializeAsync(): Promise<void> {
        await this.loadWarehousesAsync();
        await this.loadWorkOrderTypesAsync();
        await this.loadManagersAsync();
        await this.loadConstructionSitesAsync();
        await this.applyUserDefaultValuesAsync();
    }

    private async loadWarehousesAsync(): Promise<void> {
        const warehouses: Warehouse[] = await RentaTasksController.getWarehousesAsync(this);
        const userWarehouse: Warehouse | undefined = warehouses.find(w => w.id === this.userContext.user?.warehouseId);
        
        this.setState({
            warehouses,
            selectedWarehouse: userWarehouse || null
        });
    }

    private async loadWorkOrderTypesAsync(): Promise<void> {
        const dummyWorkOrder = new WorkOrderModel();
        const workOrderTypes: WorkOrderType[] = await RentaTasksController.getActiveWorkOrderTypesAsync(this, dummyWorkOrder);
        
        this.setState({
            workOrderTypes: workOrderTypes,
        });
    }

    private async loadManagersAsync(): Promise<void> {
        const managers: User[] = await this.fetchManagersAsync();
        
        this.setState({
            managers: managers,
        });
    }

    private async loadConstructionSitesAsync(): Promise<void> {
        const request = new GetConstructionSitesDataRequest();
        const allSites: ConstructionSiteOrWarehouse[] = await this.postAsync("/api/rentaTasks/getConstructionSitesData", request);
        const depotSites = allSites.filter(site => site.isDepot === true);
        
        this.setState({
            constructionSites: depotSites,
        });
    }

    private async fetchManagersAsync(): Promise<User[]> {
        return await this.postAsync("api/rentaTasks/getManagers", null);
    }

    private async applyUserDefaultValuesAsync(): Promise<void> {
        const currentUser: User = this.getUser();
        const userDefaultWorkOrderValues: UserDefaultWorkOrderValues = await this.postAsync("api/rentaTasks/getUserDefaultWorkOrderValues", currentUser.id);

        if (userDefaultWorkOrderValues) {
            if (userDefaultWorkOrderValues.depotWarehouseId) {
                const defaultWarehouse = this.state.warehouses.find(w => w.id === userDefaultWorkOrderValues.depotWarehouseId);
                if (defaultWarehouse) {
                    this.setState({ selectedWarehouse: defaultWarehouse });
                }
            }

            if (userDefaultWorkOrderValues.depotWorkOrderTypeId) {
                const defaultWorkOrderType = this.state.workOrderTypes.find(wot => wot.id === userDefaultWorkOrderValues.depotWorkOrderTypeId);
                if (defaultWorkOrderType) {
                    this.setState({ selectedWorkOrderType: defaultWorkOrderType });
                }
            }

            if (userDefaultWorkOrderValues.depotManagerId) {
                const defaultManager = this.state.managers.find(m => m.id === userDefaultWorkOrderValues.depotManagerId);
                if (defaultManager) {
                    this.setState({ selectedManager: defaultManager });
                }
            }

            if (userDefaultWorkOrderValues.depotConstructionSiteId) {
                const defaultConstructionSite = this.state.constructionSites.find(cs => cs.id === userDefaultWorkOrderValues.depotConstructionSiteId);
                if (defaultConstructionSite) {
                    this.setState({ selectedConstructionSite: defaultConstructionSite });
                }
            }
        }
    }

    private async onWarehouseChangeAsync(warehouse: Warehouse | null): Promise<void> {
        this.setState({
            selectedWarehouse: warehouse
        });
    }

    private async onHoursChangeAsync(hours: number): Promise<void> {
        this.setState({hours});
    }

    private async onCommentChangeAsync(comment: string): Promise<void> {
        this.setState({comment});
    }

    private async onWorkOrderTypeChangeAsync(workOrderType: WorkOrderType | null, userInteraction: boolean): Promise<void> {
        if (userInteraction) {
            this.setState({selectedWorkOrderType: workOrderType});
        }

    }

    private async onManagerChangeAsync(manager: User | null, userInteraction: boolean): Promise<void> {
        if (userInteraction){
            this.setState({selectedManager: manager});
        }
    }

    private async onConstructionSiteChangeAsync(constructionSite: ConstructionSiteOrWarehouse | null, userInteraction: boolean): Promise<void> {
        if (userInteraction) {
            this.setState({selectedConstructionSite: constructionSite});
        }
    }

    private async onSaveAsync(): Promise<void> {
        const { selectedWarehouse, hours, comment, selectedWorkOrderType, selectedManager, selectedConstructionSite } = this.state;
        
        if (!selectedWarehouse || hours <= 0 || !selectedManager || !selectedConstructionSite) {
            return;
        }

        this.setState({ isSaving: true });
        const request: SaveDepotWorkRequest = new SaveDepotWorkRequest();
        request.warehouseId = selectedWarehouse.id;
        request.hours = hours;
        request.comment = comment;
        request.date = new Date();
        request.workOrderTypeId = selectedWorkOrderType?.id || null;
        request.managerId = selectedManager.id;
        request.constructionSiteId = selectedConstructionSite.id;

        const response: SaveDepotWorkResponse = await RentaTasksController.saveDepotWorkAsync(this, request);

        if (response.success) {
            this.setState({
                selectedWarehouse: null,
                hours: 0,
                comment: "",
                selectedWorkOrderType: null,
                selectedManager: null,
                selectedConstructionSite: null
            });
            await PageRouteProvider.redirectAsync(PageDefinitions.rentaTasksRoute);
        } else {
            await this.alertErrorAsync(response.errorMessage || Localizer.depotWorkFailedToSave);
        }
        this.setState({ isSaving: false });
    }

    private get canSave(): boolean {
        return this.state.selectedWarehouse !== null && 
               this.state.selectedWorkOrderType !== null &&
               this.state.selectedManager !== null &&
               this.state.selectedConstructionSite !== null &&
               this.state.hours > 0 && 
               !this.state.isSaving;
    }

    public render(): React.ReactNode {
        const { selectedWarehouse, hours, comment, warehouses, isSaving, selectedWorkOrderType, workOrderTypes, selectedManager, managers, selectedConstructionSite, constructionSites } = this.state;

        return (
            <PageContainer transparent={this.desktop} className={this.css(rentaTaskStyles.pageContainer, styles.depotWork)}>
                <WidgetContainer>
                    <TitleWidget
                        wide
                        label={Localizer.depotWorkTitle}
                        icon={{ name: "fal warehouse" }}
                    />

                    <DropdownWidget<ConstructionSiteOrWarehouse>
                        wide
                        autoCollapse
                        id="constructionSite"
                        label={Localizer.genericConstructionSiteOrWarehouse}
                        items={constructionSites}
                        selectedItem={selectedConstructionSite || undefined}
                        onChange={(_, constructionSite, userInteraction) => this.onConstructionSiteChangeAsync(constructionSite, userInteraction)}
                        disabled={isSaving}
                    />

                    <DropdownWidget<Warehouse>
                        wide
                        autoCollapse
                        id="warehouse"
                        label={Localizer.topNavWarehouse}
                        items={warehouses}
                        selectedItem={selectedWarehouse || undefined}
                        onChange={(_, warehouse) => this.onWarehouseChangeAsync(warehouse)}
                        disabled={isSaving}
                    />

                    <DropdownWidget<WorkOrderType>
                        wide
                        autoCollapse
                        id="workOrderType"
                        label={Localizer.genericWorkOrderTypeLabel}
                        items={workOrderTypes}
                        selectedItem={selectedWorkOrderType || undefined}
                        onChange={(_, workOrderType, userInteraction) => this.onWorkOrderTypeChangeAsync(workOrderType, userInteraction)}
                        disabled={isSaving}
                    />

                    <DropdownWidget<User>
                        wide
                        autoCollapse
                        id="manager"
                        label={Localizer.genericManager}
                        items={managers}
                        selectedItem={selectedManager || undefined}
                        onChange={(_, manager, userInteraction) => this.onManagerChangeAsync(manager, userInteraction)}
                        disabled={isSaving}
                    />

                    <NumberWidget
                        id="hours"
                        label={Localizer.addMounterModalAddHours}
                        description={Localizer.taskHoursPanelNonBillableHours}
                        value={hours}
                        min={0}
                        max={24}
                        step={0.5}
                        onChange={(_, hours) => this.onHoursChangeAsync(hours)}
                        readonly={isSaving}
                        wide
                    />

                    <TextAreaWidget
                        wide
                        id="comment"
                        label={Localizer.genericComment}
                        value={comment}
                        onChange={(_, comment) => this.onCommentChangeAsync(comment)}
                        readonly={isSaving}
                    />

                    <Button
                        type={ButtonType.Blue}
                        className={styles.button}
                        id="saveButton"
                        label={isSaving ? "Saving..." : Localizer.formSave}
                        disabled={!this.canSave}
                        onClick={() => this.onSaveAsync()}
                    />
                </WidgetContainer>
            </PageContainer>
        );
    }
}
